import { normalizeHostname, normalizeOrigin } from '@traceio-org/platform-contracts';

/** The products this fixture stands in for. Every other product is a real application elsewhere. */
export const FIXTURE_PRODUCTS = ['carriers', 'agents'] as const;
/**
 * Hosted preview only: on hosted dev, Trace Enterprise's real application still runs on its own server with
 * its own sign-in, so a labeled skeleton fills its slot in the hub. Locally Enterprise is the real product.
 */
export const HOSTED_PREVIEW_PRODUCTS = ['carriers', 'agents', 'enterprise'] as const;
export type FixtureProduct = (typeof HOSTED_PREVIEW_PRODUCTS)[number];

/** Navigation fixtures exist only where development identity and local origins exist. */
export const FIXTURE_ENVIRONMENTS = ['development', 'test'] as const;
/**
 * Hosted dev runs with TRACE_ENV=staging (there is no other staging). A build that sets
 * VITE_HOSTED_PREVIEW=true may run there as a labeled preview skeleton; production never: the registry
 * refuses fixtures in production as well (platform-contracts applications.ts).
 */
export type FixtureEnvironment = (typeof FIXTURE_ENVIRONMENTS)[number] | 'staging';

const LOCAL_API_URL = 'http://localhost:4720';
const LOCAL_HUB_ORIGIN = 'http://localhost:4710';

export interface FixtureConfig {
  productKey: FixtureProduct;
  environment: FixtureEnvironment;
  /** A labeled preview skeleton on hosted dev (VITE_HOSTED_PREVIEW=true), not a local fixture. */
  hostedPreview: boolean;
  /** platform-api base URL (origin plus optional path, no trailing slash). */
  platformApiUrl: string;
  hubOrigin: string;
  /** The product's own Auth0 SPA registration; null means development identity. */
  auth0: { domain: string; clientId: string } | null;
}

export type FixtureConfigResult =
  { ok: true; config: FixtureConfig; warnings: string[] } | { ok: false; errors: string[] };

type Env = Record<string, string | boolean | undefined>;

function read(env: Env, name: string): string | undefined {
  const value = env[name];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function parseBaseUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.username || url.password || url.search || url.hash) return null;
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

/**
 * Validates the build-time environment once at startup. Security relevant, so spelled out: this app shows
 * a signed-in person's organization and roles, and it must never be mistaken for a product or run where
 * people use the real ones. It refuses any VITE_TRACE_ENV other than development or test (a production or
 * staging build renders the refusal page and never starts the gate), and only the Vite dev server may fall
 * back to local defaults. The registry check at startup (src/registry.ts) is the second, independent guard.
 */
export function loadFixtureConfig(env: Env, options: { dev: boolean }): FixtureConfigResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  const hostedPreview = read(env, 'VITE_HOSTED_PREVIEW') === 'true';
  const product = read(env, 'VITE_DESTINATION_PRODUCT');
  const productKey = (hostedPreview ? HOSTED_PREVIEW_PRODUCTS : FIXTURE_PRODUCTS).find((p) => p === product);
  if (!productKey) {
    errors.push(
      hostedPreview
        ? 'VITE_DESTINATION_PRODUCT must be carriers, agents or enterprise.'
        : 'VITE_DESTINATION_PRODUCT must be carriers or agents.',
    );
  }

  let environment: FixtureEnvironment | undefined;
  const rawEnvironment = read(env, 'VITE_TRACE_ENV');
  if (rawEnvironment === undefined) {
    if (options.dev) {
      environment = 'development';
      warnings.push('VITE_TRACE_ENV is not set; using development.');
    } else {
      errors.push('VITE_TRACE_ENV is required (development or test).');
    }
  } else {
    environment = FIXTURE_ENVIRONMENTS.find((e) => e === rawEnvironment);
    // Security relevant: staging (hosted dev) only for an explicitly labeled preview build; production never.
    if (!environment && hostedPreview && rawEnvironment === 'staging') environment = 'staging';
    if (!environment) {
      errors.push(
        `VITE_TRACE_ENV is ${rawEnvironment}: a navigation fixture runs only in development and test, never where the products are deployed.`,
      );
    }
  }

  let platformApiUrl: string | undefined;
  const rawApiUrl = read(env, 'VITE_PLATFORM_API_URL');
  if (rawApiUrl === undefined) {
    if (options.dev) {
      platformApiUrl = LOCAL_API_URL;
      warnings.push(`VITE_PLATFORM_API_URL is not set; using ${LOCAL_API_URL}.`);
    } else {
      errors.push('VITE_PLATFORM_API_URL is required.');
    }
  } else {
    platformApiUrl = parseBaseUrl(rawApiUrl) ?? undefined;
    if (!platformApiUrl)
      errors.push('VITE_PLATFORM_API_URL must be an http or https URL without a query or fragment.');
  }

  let hubOrigin: string | undefined;
  const rawHubOrigin = read(env, 'VITE_HUB_ORIGIN');
  if (rawHubOrigin === undefined) {
    if (options.dev) {
      hubOrigin = LOCAL_HUB_ORIGIN;
      warnings.push(`VITE_HUB_ORIGIN is not set; using ${LOCAL_HUB_ORIGIN}.`);
    } else {
      errors.push('VITE_HUB_ORIGIN is required.');
    }
  } else {
    try {
      hubOrigin = normalizeOrigin(rawHubOrigin);
    } catch {
      errors.push('VITE_HUB_ORIGIN must be an origin such as http://localhost:4710.');
    }
  }

  let auth0: FixtureConfig['auth0'] = null;
  const domain = read(env, 'VITE_AUTH0_DOMAIN');
  const clientId = read(env, 'VITE_AUTH0_CLIENT_ID');
  if (domain && clientId) {
    // The SDK expects a bare host (tenant or custom domain), never a URL.
    const host = normalizeHostname(domain);
    if (!host.ok || host.hostname !== domain.toLowerCase()) {
      errors.push('VITE_AUTH0_DOMAIN must be a host name such as trace-dev.us.auth0.com.');
    } else if (!/^[A-Za-z0-9_-]{1,128}$/.test(clientId)) {
      errors.push('VITE_AUTH0_CLIENT_ID has unexpected characters.');
    } else {
      auth0 = { domain, clientId };
    }
  } else if (domain || clientId) {
    const missing = domain ? 'VITE_AUTH0_CLIENT_ID' : 'VITE_AUTH0_DOMAIN';
    warnings.push(`Auth0 is only partly configured (missing ${missing}); using development identity.`);
  }

  if (errors.length > 0 || !productKey || !environment || !platformApiUrl || !hubOrigin) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    warnings,
    config: { productKey, environment, hostedPreview, platformApiUrl, hubOrigin, auth0 },
  };
}
